import React from 'react';
import { AlertTriangle, Ban, Leaf, Sparkles } from 'lucide-react';

function titleCaseSlug(value) {
  return String(value || '')
    .replace(/^avoided_/, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function cleanProfileLabel(title) {
  return String(title || '')
    .replace(/^My Diet:\s*/i, '')
    .replace(/\s+profile$/i, '')
    .replace(/^./, (char) => char.toUpperCase());
}

function extractAfterColon(title) {
  const parts = String(title || '').split(':');
  return parts.length > 1 ? parts.slice(1).join(':').trim() : '';
}

function groupMatches(matches) {
  const groups = [
    {
      id: 'allergen_watch',
      category: 'allergen_watch',
      title: 'Allergen Watch',
      values: [],
      detail: 'On your watch list.',
    },
    {
      id: 'avoided',
      category: 'avoided',
      title: 'Avoid',
      values: [],
      detail: 'On your no-thanks list.',
    },
    {
      id: 'my_diet',
      category: 'my_diet',
      title: 'My Diet',
      values: [],
      detail: 'Doesn’t match your profile — verdict is above.',
    },
  ];
  const groupByCategory = new Map(groups.map((group) => [group.category, group]));
  const other = { id: 'other', category: 'other', title: 'Food Profile', values: [] };

  matches.forEach((match) => {
    if (match.id === 'allergen_watch') {
      const value = extractAfterColon(match.title);
      if (value) {
        groupByCategory.get('allergen_watch').values.push(
          ...value.split(',').map((item) => item.trim()).filter(Boolean)
        );
      }
      if (match.detail) groupByCategory.get('allergen_watch').detail = match.detail;
      return;
    }

    if (String(match.id || '').startsWith('avoided_')) {
      groupByCategory.get('avoided').values.push(extractAfterColon(match.title) || titleCaseSlug(match.id.replace(/^avoided_/, '')));
      if (match.detail) groupByCategory.get('avoided').detail = match.detail;
      return;
    }

    if (['vegan', 'vegetarian', 'dairy_free', 'gluten_aware'].includes(match.id)) {
      groupByCategory.get('my_diet').values.push(cleanProfileLabel(match.title));
      if (match.detail) groupByCategory.get('my_diet').detail = match.detail;
      return;
    }

    if (match.title) other.values.push(match.title);
  });

  return [...groups, other].filter((group) => group.values.length > 0);
}

const GROUP_ICONS = {
  allergen_watch: AlertTriangle,
  avoided: Ban,
  my_diet: Leaf,
};

// These quips stay tied to why the row exists. The watch sentence now carries
// the alert exclamation, so its quips use their natural punctuation instead.
const WATCH_QUIPS = [
  'Better here than mid-bite.',
  "The label won't mention your watch list. We will.",
  'Consider this your pre-snack PSA.',
  'Your future self says thanks.',
];

const AVOID_QUIPS = [
  "Your no-thanks list doesn't negotiate.",
  'Not telling you what to do. Just… you know.',
  'You said avoid. The ingredient said hello.',
  'Consider this your polite hard pass.',
];

const DIET_LABELS = {
  gluten_aware: 'Gluten-aware',
  dairy_free: 'Dairy-free',
  vegetarian: 'Vegetarian',
  vegan: 'Vegan',
};

const DIET_QUIPS = {
  gluten_aware: [
    'Gluten hides. Labels boast. You be the judge.',
    'When the front says fancy, the back says read me.',
  ],
  dairy_free: [
    'Dairy loves a disguise. Worth a peek.',
    "Creamy wording isn't always plant-based.",
  ],
  vegetarian: [
    "Veggie-friendly and vegetarian-safe aren't twins.",
    'Gelatin never sends a warning email.',
  ],
  vegan: [
    '"Plant-based" and vegan aren\'t always roommates.',
    'If it had a face, your profile would care.',
  ],
};

function stableHash(value) {
  let hash = 0;
  for (const char of String(value || '')) {
    hash = ((hash * 31) + char.charCodeAt(0)) >>> 0;
  }
  return hash;
}

function stableQuip(pool, seed) {
  return pool[stableHash(seed) % pool.length];
}

function naturalList(values) {
  if (values.length <= 1) return values[0] || '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

function capitalizeLabel(value) {
  const label = String(value || '').trim();
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
}

function runningTextLabel(value) {
  const label = String(value || '').trim();
  if (!label) return '';
  if (/[A-Z]/.test(label) && label === label.toUpperCase()) return label;
  return label.charAt(0).toLowerCase() + label.slice(1);
}

function dietPreset(value) {
  const id = String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  return DIET_LABELS[id] ? id : null;
}

function displayRows(groups, productMatch) {
  const rows = [];
  const categoryValues = (category) => [...new Set(
    groups
      .filter((group) => group.category === category)
      .flatMap((group) => Array.isArray(group.values) ? group.values : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )];

  const watchValues = categoryValues('allergen_watch');
  if (watchValues.length > 0) {
    const names = naturalList(watchValues.map((value, index) => (
      index === 0 ? capitalizeLabel(value) : runningTextLabel(value)
    )));
    const plural = watchValues.length > 1;
    rows.push({
      key: 'allergen_watch',
      category: 'allergen_watch',
      sentence: `${names} ${plural ? 'are' : 'is'} on your watch list, so we're flagging ${plural ? 'them' : 'it'} here!`,
      quip: stableQuip(WATCH_QUIPS, `${productMatch}|allergen_watch|${watchValues.join('|')}`),
    });
  }

  const avoidedValues = categoryValues('avoided');
  if (avoidedValues.length > 0) {
    const names = naturalList(avoidedValues.map(runningTextLabel));
    rows.push({
      key: 'avoided',
      category: 'avoided',
      sentence: `Since you're avoiding ${names}, we're calling ${avoidedValues.length > 1 ? 'them' : 'it'} out.`,
      quip: stableQuip(AVOID_QUIPS, `${productMatch}|avoided|${avoidedValues.join('|')}`),
    });
  }

  const dietPresets = [...new Set(categoryValues('my_diet').map(dietPreset).filter(Boolean))];
  if (dietPresets.length > 0) {
    const names = naturalList(dietPresets.map((preset) => DIET_LABELS[preset].toLowerCase()));
    const quipPool = dietPresets.flatMap((preset) => DIET_QUIPS[preset]);
    rows.push({
      key: 'my_diet',
      category: 'my_diet',
      sentence: `You're ${names}, so heads-up!`,
      quip: stableQuip(quipPool, `${productMatch}|my_diet|${dietPresets.join('|')}`),
    });
  }
  return rows;
}

export function ForYouCard({ data, variant = 'default', productMatch = '' }) {
  const matches = Array.isArray(data?.matches) ? data.matches : [];
  const groups = Array.isArray(data?.groups) && data.groups.length > 0
    ? data.groups
    : groupMatches(matches);
  const rows = displayRows(groups, productMatch);
  if (rows.length === 0) return null;

  const isDesktopQuadrant = variant === 'desktop-quadrant';
  const rootClassName = isDesktopQuadrant
    ? 'w-full min-w-0 rounded-[1.75rem] border border-[#f0a24a] bg-[#fff3df] p-5 text-[#80400f]'
    : 'mb-4 rounded-[1.75rem] border border-[#f0a24a] bg-[#fff3df] p-5 text-[#80400f]';

  return (
    <section className={rootClassName}>
      <div>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center text-[#e2761b]">
            <Sparkles className="h-4.5 w-4.5" />
          </span>
          <div>
            <h3 className="text-xl font-semibold">Your Plate</h3>
            <p className="mt-1 text-sm font-medium leading-6 text-[#80400f]">
              Careful — this product matched your Food Profile. Please take a closer look before you dig in!
            </p>
          </div>
        </div>
      </div>
      <div>
        <div className="mt-4 divide-y divide-[#f0a24a]/35">
          {rows.map((row) => {
            const Icon = GROUP_ICONS[row.category] || Sparkles;
            return (
              <div key={row.key} data-profile-row={row.category} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full border border-[#e2761b]/35 bg-[#fff3df]/70 text-[#e2761b]">
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-6 text-[#80400f]">
                    {row.sentence}
                  </p>
                  <p className="mt-1 text-sm font-medium leading-6 text-[#9a531b]">
                    {row.quip}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <footer className="mt-4 text-xs font-medium leading-5 text-[#80400f]">
          Think of this as your extra safety net. The main result didn't change.
        </footer>
      </div>
    </section>
  );
}
